import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Image as ImageIcon, Loader2, Lock, Megaphone, Paperclip, Users, X } from "lucide-react";
import { api, formatErr, timeAgo } from "@/lib/api";

const chip = (on) => `rounded-sm border px-3 py-1.5 text-xs font-medium tabular-nums transition-colors ${
  on ? "border-white bg-white text-[#090A0C]" : "border-[#2A2E37] text-gray-300 hover:border-white/30"}`;

function Composer({ subscribers, onSent }) {
  const [text, setText] = useState("");
  const [paid, setPaid] = useState(false);
  const [prices, setPrices] = useState([]);
  const [priceKey, setPriceKey] = useState("unlock_500");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/unlock-prices").then((r) => setPrices(r.data)); }, []);
  const price = prices.find((p) => p.lookup_key === priceKey)?.amount;

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() && !file) return toast.error("Write a message or attach a photo/video");
    const fd = new FormData();
    fd.append("text", text);
    if (paid) fd.append("lookup_key", priceKey);
    if (file) fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/broadcasts", fd);
      toast.success(`Sent to ${data.recipients} subscriber${data.recipients === 1 ? "" : "s"}`);
      setText("");
      setFile(null);
      onSent(data);
    } catch (err) { toast.error(formatErr(err)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={send} className="rounded-md border border-[#2A2E37] bg-[#121418] p-5" data-testid="broadcast-composer">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
          <button type="button" onClick={() => setPaid(false)} className={chip(!paid)} data-testid="broadcast-free-btn">Free</button>
          <button type="button" onClick={() => setPaid(true)} className={`${chip(paid)} flex items-center gap-1.5`} data-testid="broadcast-paid-btn">
            <Lock className="h-3 w-3" /> Paid
          </button>
        </div>
        <span className="flex items-center gap-1.5 text-xs text-gray-400" data-testid="broadcast-recipients">
          <Users className="h-3.5 w-3.5" /> Sends to {subscribers} subscriber{subscribers === 1 ? "" : "s"}
        </span>
      </div>
      {paid && (
        <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="broadcast-prices">
          <span className="mr-1 text-xs text-gray-500">Unlock price</span>
          {prices.map((p) => (
            <button key={p.lookup_key} type="button" onClick={() => setPriceKey(p.lookup_key)} className={chip(priceKey === p.lookup_key)}
              data-testid={`broadcast-price-${p.lookup_key}`}>${p.amount}</button>
          ))}
        </div>
      )}
      <textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} data-testid="broadcast-text-input"
        placeholder={paid ? "Tease what's inside. Subscribers see the text only after they unlock." : "Write to all your subscribers..."}
        className="mt-4 min-h-[120px] w-full rounded-sm border border-[#2A2E37] bg-white/5 px-3.5 py-3 text-sm outline-none focus:border-white/40" />
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <label className="flex cursor-pointer items-center gap-1.5 text-xs text-gray-300 hover:text-white">
          <Paperclip className="h-3.5 w-3.5" /> {file ? <span className="max-w-[200px] truncate">{file.name}</span> : "Attach photo or video"}
          <input type="file" accept="image/*,video/*" className="hidden" data-testid="broadcast-file-input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </label>
        {file && <button type="button" onClick={() => setFile(null)} aria-label="Remove attachment" className="text-gray-500 hover:text-white" data-testid="broadcast-file-clear"><X className="h-3.5 w-3.5" /></button>}
        <button type="submit" disabled={busy || subscribers === 0} data-testid="broadcast-send-btn"
          className="btn-neon ml-auto flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Megaphone className="h-4 w-4" />}
          {paid ? `Send paid · $${price ?? ""}` : "Send to all"}
        </button>
      </div>
    </form>
  );
}

export default function Broadcast() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => { api.get("/broadcasts").then((r) => setData(r.data)).catch((e) => setError(formatErr(e))); }, []);

  if (error) return <p className="p-16 text-center text-sm text-gray-400" data-testid="broadcast-error">{error}</p>;
  if (!data) return <p className="p-16 text-center text-sm text-gray-500">Loading...</p>;

  return (
    <div className="mx-auto max-w-3xl px-4 py-10 md:px-8 md:py-14" data-testid="broadcast-page">
      <Link to="/dashboard" className="flex items-center gap-1 text-xs text-gray-400 hover:text-white" data-testid="broadcast-back-link"><ArrowLeft className="h-3.5 w-3.5" /> Dashboard</Link>
      <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.3em] text-[#FF2E4D]">Mass message</p>
      <h1 className="mt-2 text-2xl font-semibold tracking-tight sm:text-3xl">Message all subscribers</h1>
      <p className="mt-1 text-sm text-gray-400">Lands in each subscriber's inbox as a private message.</p>
      <div className="mt-8"><Composer subscribers={data.subscribers_count} onSent={(b) => setData((d) => ({ ...d, items: [b, ...d.items] }))} /></div>
      <h2 className="mt-10 text-sm font-semibold tracking-tight">Sent</h2>
      <div className="mt-3 divide-y divide-[#2A2E37] rounded-md border border-[#2A2E37] bg-[#121418]" data-testid="broadcast-history">
        {data.items.length === 0 && <p className="px-5 py-8 text-center text-sm text-gray-500" data-testid="broadcast-history-empty">No mass messages yet.</p>}
        {data.items.map((b) => (
          <div key={b.id} className="flex items-start gap-4 px-5 py-4" data-testid="broadcast-row">
            <div className="min-w-0 flex-1">
              <p className="line-clamp-2 text-sm text-gray-100">{b.text || <span className="text-gray-500">Attachment only</span>}</p>
              <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500">
                <span>{timeAgo(b.created_at)}</span>
                <span>{b.recipients} recipients</span>
                {b.media_type && <span className="flex items-center gap-1"><ImageIcon className="h-3 w-3" />{b.media_type}</span>}
              </p>
            </div>
            <div className="text-right">
              {b.price > 0 ? (
                <>
                  <p className="flex items-center justify-end gap-1 text-sm tabular-nums"><Lock className="h-3 w-3 text-[#FF2E4D]" />${b.price.toFixed(2)}</p>
                  <p className="text-[11px] tabular-nums text-gray-500" data-testid="broadcast-unlocks">{b.unlocks} unlocked · ${b.revenue.toFixed(2)}</p>
                </>
              ) : <span className="font-mono text-[10px] uppercase tracking-wider text-gray-500">Free</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
